import { Stack, Select, MultiSelect, Checkbox, Box, Button, Group, Space } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { UseFormReturnType } from '@mantine/form';
import { IconCalendar, IconSearch } from '@tabler/icons-react';

import { analyticsTypes } from '../../types';

import { managerTypes, useGetManagers } from '@/features/managers';
import { DATE_VALUES } from '@/utils/dates';

const formatData = ({ data }: { data?: { managers: managerTypes.Manager[] } }) => {
  if (!data || !data.managers) {
    return { selectOptions: [], selectOptionsList: [] };
  }
  const managers = data.managers.map((i) => ({ label: i.username, value: i._id, type: 'manager' }));
  const departments = Array.from(new Set(data.managers.map((i) => i.department))).map((i) => ({
    label: i,
    value: i,
    type: 'department',
  }));
  return {
    selectOptions: [
      { group: 'Departments', items: departments },
      { group: 'Managers', items: managers },
    ],
    selectOptionsList: [...departments, ...managers],
  };
};

type Props = {
  form: UseFormReturnType<analyticsTypes.GetManagerRepliesParams>;
  onSubmit: () => void;
};

export const FilterForm = ({ form, onSubmit }: Props) => {
  const { data } = useGetManagers();
  const { selectOptions, selectOptionsList } = formatData({ data });
  const periodDuration = form.values.dateTo.valueOf() - form.values.dateFrom.valueOf();

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack justify="space-between">
        <Group grow>
          <DatePickerInput
            required
            maxDate={form.values.dateTo}
            leftSection={<IconCalendar size={20} stroke={1.5} />}
            label="Select the start of the time period:"
            {...form.getInputProps('dateFrom')}
          />
          <DatePickerInput
            required
            minDate={form.values.dateFrom}
            leftSection={<IconCalendar size={20} stroke={1.5} />}
            label="Select the end of the time period:"
            {...form.getInputProps('dateTo')}
          />
        </Group>
        <Group grow>
          <Select
            required
            label="Select display interval:"
            allowDeselect={false}
            data={[
              { label: 'Month', value: 'month', disabled: periodDuration > DATE_VALUES.year },
              { label: 'Week', value: 'week', disabled: periodDuration > DATE_VALUES.month * 6 },
              { label: 'Day', value: 'day', disabled: periodDuration > DATE_VALUES.month * 3 },
              { label: 'Hour', value: 'hour', disabled: periodDuration > DATE_VALUES.month },
            ]}
            {...form.getInputProps('interval')}
          />
          <MultiSelect
            required
            label="Select display employee/department:"
            data={selectOptions}
            value={form.values.targets.departments.concat(form.values.targets.managers)}
            onChange={(values) => {
              const selected = selectOptionsList.filter((i) => values.includes(i.value));
              const departments = selected.filter((i) => i.type === 'department');
              const managers = selected.filter((i) => i.type === 'manager');
              form.setFieldValue('targets', {
                departments: departments.map((i) => i.value),
                managers: managers.map((i) => i.value),
              });
            }}
            error={form.errors.targets}
            searchable
            placeholder="Search..."
          />
        </Group>

        <Checkbox
          variant="outline"
          label="Group messages from the same dialogues"
          checked={form.values.isUniqueDialogs}
          onChange={(event) => form.setFieldValue('isUniqueDialogs', event.currentTarget.checked)}
        />
      </Stack>
      <Space h="md" />
      <Box>
        <Button
          type="submit"
          variant="outline"
          color="green"
          leftSection={<IconSearch size={20} stroke={1.5} />}
        >
          Apply filters
        </Button>
      </Box>
    </form>
  );
};
